import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Trash } from '@phosphor-icons/react';
import CartIcon from '../shared/CartIcon';
import confetti from 'canvas-confetti';
import { useCart } from '../../hooks/useCart';
import { useBusinessesContext } from '../../context/BusinessesContext';
import ConfirmModal from '../shared/ConfirmModal';

const buildImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `https://${url}`;
};

const slugify = (name) =>
  (name || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

/**
 * Cart summary bar para la HOME — diseño rico con:
 *   1. Imagen del PRIMER producto agregado al carrito (no logo del negocio)
 *   2. Nombre del negocio + link "Ver menú" → navega al business
 *   3. Pill morada con item count + total + "VER CARRITO" + ícono → /cart
 *   4. Botón trash rojo que vacía el carrito (con toast)
 *
 * Wrapper: card blanca pinned al bottom con esquinas superiores redondeadas,
 * sombra mínima hacia arriba. El pill morado tiene su propia sombra hacia
 * abajo para destacar como CTA principal.
 *
 * Para la versión simple usada en la pantalla de menú de negocio, ver
 * la hoja del carrito.
 */
export default function HomeCartSummary() {
  const navigate = useNavigate();
  const { items, itemCount, subtotal, clearCart } = useCart();
  const { businesses } = useBusinessesContext();
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [voiceSheetOpen, setVoiceSheetOpen] = useState(false);
  // visible controla la animación slide-down/up al hacer scroll.
  const [visible, setVisible] = useState(true);
  // Ref para broadcast sincrónico (sin esperar al useEffect post-commit).
  const visibleRef = useRef(true);
  // Ref del pill "VER CARRITO" — desde su posición sale el confetti cuando
  // se agrega el primer producto al carrito.
  const ctaPillRef = useRef(null);
  // Trackea el itemCount anterior para detectar la transición exacta 0 → 1+.
  // Se inicializa con el itemCount actual para evitar disparar confetti en
  // mounts donde el carrito ya tenía items (vuelta desde otra pantalla,
  // hidratación de localStorage al abrir la app).
  const prevItemCountRef = useRef(itemCount);

  // Actualiza estado + broadcast en la MISMA microtask. Eso elimina el frame
  // de delay entre el cart bar y los floating sibilings (badge, back-to-top)
  // que escuchan el evento — todos animan exactamente al mismo tiempo.
  const updateVisibility = useCallback((next) => {
    if (visibleRef.current === next) return;
    visibleRef.current = next;
    setVisible(next);
    window.dispatchEvent(new CustomEvent('homeCartBarVisibility', { detail: { visible: next } }));
  }, []);

  // Escucha el sheet de búsqueda por voz para ocultarse mientras esté abierto
  // (si no, se ve detrás del backdrop oscuro del sheet).
  useEffect(() => {
    const handler = (e) => setVoiceSheetOpen(!!e.detail?.open);
    window.addEventListener('voiceSheetToggle', handler);
    return () => window.removeEventListener('voiceSheetToggle', handler);
  }, []);

  // Auto-hide al scrollear hacia abajo, mostrar al scrollear hacia arriba.
  // Cerca del top siempre visible. Ignora micro-movimientos (< DELTA_MIN).
  useEffect(() => {
    let lastY = window.scrollY;
    let rafId = null;
    const DELTA_MIN = 8;
    const THRESHOLD_HIDE = 120;

    const handleScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        const currentY = window.scrollY;
        const delta = currentY - lastY;

        if (Math.abs(delta) < DELTA_MIN) {
          rafId = null;
          return;
        }

        if (currentY < THRESHOLD_HIDE) {
          updateVisibility(true);
        } else if (delta > 0) {
          updateVisibility(false);
        } else {
          updateVisibility(true);
        }

        lastY = currentY;
        rafId = null;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [updateVisibility]);

  // Cuando agrega/cambia algo del carrito, forzar visible aunque esté
  // scrolleado hacia abajo. Si scrollea después se vuelve a ocultar.
  useEffect(() => {
    updateVisibility(true);
  }, [itemCount, updateVisibility]);

  // Confetti SOLO en la transición itemCount 0 → 1+ (primer item agregado
  // mientras el componente ya está montado en Home). El componente NO se
  // desmonta cuando items.length === 0 (solo retorna null), entonces el
  // mount fire-once no detecta el add real — necesitamos comparar contra
  // el valor anterior. rAF para que el pill ya esté pintado antes de medir.
  useEffect(() => {
    const prev = prevItemCountRef.current;
    prevItemCountRef.current = itemCount;
    if (prev !== 0 || itemCount === 0) return;
    requestAnimationFrame(() => {
      if (!ctaPillRef.current) return;
      const rect = ctaPillRef.current.getBoundingClientRect();
      confetti({
        particleCount: 60,
        spread: 70,
        startVelocity: 35,
        ticks: 200,
        shapes: ['square'],
        origin: {
          x: (rect.left + rect.width / 2) / window.innerWidth,
          y: (rect.top + rect.height / 2) / window.innerHeight,
        },
        colors: ['#4b0082', '#B5F800', '#ffffff'],
      });
    });
  }, [itemCount]);

  if (items.length === 0 || voiceSheetOpen) return null;

  const firstItem = items[0];
  const businessId = firstItem?.businessId || firstItem?.product?.businessId;
  const business = businesses?.find(b => (b._id || b.id) === businessId);
  const businessName = business?.name || 'Tu pedido';
  const businessSlug = business?.slug || slugify(business?.name);

  // Nombre e imagen del ÚLTIMO producto agregado (el carrito agrega al
  // final): es lo que el usuario acaba de tocar. Con un solo restaurante,
  // el nombre del negocio ahí no decía nada.
  const lastItem = items[items.length - 1];
  const productName = lastItem?.product?.name || businessName;
  const productImage = lastItem?.image || lastItem?.product?.image;
  const firstProductImg = buildImageUrl(productImage);

  const handleViewCart = () => navigate('/cart');
  const handleViewMenu = (e) => {
    e.stopPropagation();
    if (businessSlug) navigate(`/business/${businessSlug}`, { state: { business } });
  };
  const handleTrashClick = (e) => {
    e.stopPropagation();
    setShowClearConfirm(true);
  };

  const confirmClear = () => {
    clearCart(true);
    setShowClearConfirm(false);
  };

  // Portal a body para escapar cualquier transform ancestor (slider de
  // MarketLayout). Sin esto, `fixed bottom-0` se posiciona relativo al
  // slider en vez del viewport.
  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      className={`fixed bottom-0 left-0 right-0 z-40 bg-white rounded-t-3xl shadow-up px-3 pt-3 transition-transform duration-300 ease-out ${
        visible ? 'translate-y-0' : 'translate-y-full'
      }`}
      style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 12px)' }}
    >
      <div className="flex items-center gap-2">
        {/* Imagen del último producto agregado */}
        <div className="w-12 h-12 rounded-2xl overflow-hidden bg-gray-100 flex-shrink-0">
          {firstProductImg ? (
            <img src={firstProductImg} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-2xl">🍽️</div>
          )}
        </div>

        {/* Nombre del último producto agregado + Ver menú */}
        <div className="flex-1 min-w-0">
          <p className="text-[16px] font-semibold text-gray-800 truncate leading-none">
            {productName}
          </p>
          {businessSlug && (
            <button
              onClick={handleViewMenu}
              className="text-[13px] font-medium text-cta-bg underline underline-offset-2 mt-0.5 leading-none"
            >
              Ver menú
            </button>
          )}
        </div>

        {/* CTA pill — sombra hacia abajo para destacar. Su posición es el
            origen del confetti al primer item del carrito. */}
        <button
          ref={ctaPillRef}
          onClick={handleViewCart}
          className="bg-cta-bg hover:bg-cta-hover text-white rounded-full pl-4 pr-3 py-2 flex items-center gap-2 shadow-md shadow-black/20 flex-shrink-0 active:scale-[0.98] transition-transform"
        >
          <div className="text-left leading-tight">
            <p className="text-[10px] font-semibold opacity-70 whitespace-nowrap">
              {itemCount} {itemCount === 1 ? 'item' : 'items'} | ${subtotal.toFixed(0)}
            </p>
            <p className="text-[12px] font-semibold tracking-wide">VER CARRITO</p>
          </div>
          <CartIcon size={22} />
        </button>

        {/* Trash — pide confirmación antes de vaciar */}
        <button
          onClick={handleTrashClick}
          className="w-11 h-11 bg-red-100 rounded-2xl flex items-center justify-center flex-shrink-0 active:scale-95 transition-transform"
          aria-label="Vaciar carrito"
        >
          <Trash size={20} weight="bold" className="text-red-500" />
        </button>
      </div>

      <ConfirmModal
        isOpen={showClearConfirm}
        onClose={() => setShowClearConfirm(false)}
        onConfirm={confirmClear}
        title="¿Vaciar carrito?"
        message={
          <>
            ¿Estás seguro de vaciar tu carrito de <span className="font-semibold text-gray-900">{businessName}</span>?
          </>
        }
        confirmLabel="Sí"
        cancelLabel="No"
      />
    </div>,
    document.body
  );
}
